import { isFulfilled } from '@reduxjs/toolkit';
import { FC, memo, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Loader from '../../components/Loader';
import { STORAGE_KEYS } from '../../constants/storageKeys';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import { useAppDispatch as useDispatch } from '../../store';
import { getMeThunk } from '../../store/auth/thunks';

/**
 * ImpersonationEntry Component
 * 
 * This page handles the impersonation token from URL params,
 * sets up the impersonation session, and redirects to the target page.
 * 
 * URL params:
 * - impersonation_token: The JWT token for impersonation
 * - session: The session UUID for tracking
 * - redirect: (optional) The path to redirect after setup, defaults to /candidate/my-profile/id-verification
 */
const ImpersonationEntry: FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string>('Validating access link...');
  const { refreshImpersonationData } = useImpersonation();

  useEffect(() => {
    const setupImpersonation = async () => {
      const token = searchParams.get('impersonation_token');
      const sessionUuid = searchParams.get('session');
      const redirectPath = decodeURIComponent(searchParams.get('redirect') || '/candidate/my-profile/id-verification');

      if (!token || !sessionUuid) {
        setError('Invalid impersonation link. Missing token or session information.');
        return;
      }

      try {
        // Decode the token to get expiry and user info
        const tokenParts = token.split('.');
        if (tokenParts.length !== 3) {
          throw new Error('Invalid token format');
        }

        const tokenPayload = JSON.parse(atob(tokenParts[1]));
        const expiresAt = new Date(tokenPayload.exp * 1000).toISOString();
        const now = new Date();

        // Check if token has expired
        if (now >= new Date(expiresAt)) {
          setError('This impersonation session has expired. Please request a new access link.');
          return;
        }

        setStatusMessage('Setting up impersonation session...');

        // Store impersonation data in sessionStorage
        const impersonationData = {
          token: token,
          tokenType: 'bearer',
          expiresIn: tokenPayload.exp - Math.floor(Date.now() / 1000),
          expiresAt: expiresAt,
          sessionUuid: sessionUuid,
          candidate: {
            uuid: tokenPayload.user?.uuid || '',
            name: `${tokenPayload.user?.first_name || ''} ${tokenPayload.user?.last_name || ''}`.trim(),
            email: tokenPayload.user?.email || '',
          },
          isImpersonation: true,
          redirectPath: redirectPath,
        };

        // Store the impersonation data in sessionStorage (used by axios interceptor)
        sessionStorage.setItem(STORAGE_KEYS.IMPERSONATION_DATA, JSON.stringify(impersonationData));

        // NOTE: We intentionally do NOT store the impersonation token in localStorage
        // The axios interceptor checks sessionStorage first for impersonation token
        // This keeps the admin's original token safe in localStorage

        setStatusMessage('Loading candidate profile...');

        // Fetch user data with the impersonation token
        const result = await dispatch(getMeThunk());
        
        if (isFulfilled(result)) {
          const userData = result.payload?.data?.user;
          
          // Update impersonation data with actual candidate info from API response
          const updatedImpersonationData = {
            ...impersonationData,
            candidate: {
              uuid: userData?.uuid || '',
              name: `${userData?.first_name || ''} ${userData?.last_name || ''}`.trim() || 'Candidate',
              email: userData?.email || '',
            },
          };
          sessionStorage.setItem(STORAGE_KEYS.IMPERSONATION_DATA, JSON.stringify(updatedImpersonationData));
          
          // Refresh the context with updated data
          refreshImpersonationData();
          
          setStatusMessage('Redirecting to candidate profile...');
          
          // Delay to ensure Redux state is fully propagated to components
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Navigate to the target page
          // Use replace to prevent going back to this entry page
          navigate(redirectPath, { replace: true });
        } else {
          console.error('Impersonation - getMeThunk failed:', result);
          throw new Error('Failed to load candidate data');
        }

      } catch (err) {
        console.error('Failed to setup impersonation:', err);
        setError('Failed to setup impersonation session. The link may be invalid or expired.');
      }
    };

    setupImpersonation();
  }, [searchParams, navigate, dispatch, refreshImpersonationData]);

  if (error) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center min-vh-100 p-4">
        <div className="text-center">
          <div className="fs-24 fw-600 text-alert mb-3">Access Error</div>
          <p className="fs-16 text-grey-dark mb-4">{error}</p>
          <p className="fs-14 text-grey-medium">
            Please close this tab and request a new access link from the admin panel.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column align-items-center justify-content-center min-vh-100">
      <Loader />
      <p className="mt-3 fs-16 text-grey-dark">{statusMessage}</p>
    </div>
  );
};

export default memo(ImpersonationEntry);
